import type { ComponentType, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { CardHeader, Housing } from './Card'
import { ChartFocusProvider, useChartFocus } from './chart-focus'
import { usePublishPageMeta } from '@/layout/page-meta'
import { Lamp, type LampState } from './instrument'
import { Badge } from './primitives'
import { RoutedMark } from './loading-indicator'
import { TONE_TEXT, type Tone } from './readout'
import { PAGE_ART } from './page-art'
import type { NavIcon } from './icons'

/**
 * The page frame.
 *
 * ── Why this exists ──
 * Every page in this app was opening differently. Six pages led with an engraved station
 * plate (`ST-06 · LEND`), Markets and Lite each led with their own 48px centred marketing
 * hero, and bNDX and Transparency led with no header at all — they simply started on a
 * section rule or a card. Five sibling tabs inside one section, five different first
 * impressions. Nothing was individually wrong; the set was incoherent, and that is what a
 * user reads as "this product was built by several people who never met".
 *
 * The reference the direction came from (defi.pots.money) is disciplined about exactly this:
 * its Bonding and Staking pages are the same frame down to the pixel, and only the content
 * inside differs. Two of its three hero metric cards are literally repeated between pages.
 * The lesson is not its palette — we keep our own silver and enamel — it is that the frame
 * is a constant, not a per-page decision.
 *
 * So: one header, one metric row, one surface. A page composes them and then gets on with
 * being a page.
 *
 * ── What the station plate cost ──
 * The plate carried the landing page's machine-hall identity, which is a real thing to give
 * up. Two problems outweighed it. The `ST-nn` sequence had holes — Lite, Markets, bNDX and
 * Transparency never had numbers — so it advertised an order that did not exist, and a
 * numbered marker is only honest when the content genuinely is a sequence. And a milled
 * plate reading `ST-07 · BORROW` names the page in a vocabulary nobody navigates by; the rail
 * says "Borrow", the tab says "Borrow", and the page said `ST-07`.
 *
 * The identity survives where it is load-bearing: the rail's enamel tiles, the housings, the
 * lamps, the readouts. It is the page title that goes back to naming the page.
 */

/**
 * Page header — the hero. A glyph beside the title and subtitle, over a dotted field, which
 * is how the reference (defi.pots.money) opens every page.
 *
 * ── The page is still the one place a title is written down ──
 * Pages pass `icon` and `title` here, and this component both renders them and publishes
 * them upward to the app bar (see layout/page-meta.tsx) via `usePublishPageMeta`. That was
 * the original reason for not putting a route-to-title map in the shell instead — a page
 * that gets renamed only has one place to edit — and it still holds now that the title
 * renders here too.
 *
 * ── The title used to appear twice ──
 * For a time the bar also rendered the title it was published, alongside the hero rendering
 * it here — a known, temporary intermediate state from restoring the hero before the bar's
 * copy was removed. The bar's copy is gone now, so the title appears once, here.
 *
 * ── The glyph ──
 * `Art` resolves through `PAGE_ART`, a second, larger icon system keyed by the rail icon's
 * stable `iconId` (see ui/page-art.tsx and the `iconId` note in ui/icons.tsx), falling back
 * to the rail icon itself if no page-header art is registered for it.
 */
export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  state,
  action,
}: {
  /** The rail's icon for this destination. See ui/icons.tsx. */
  icon: ComponentType<{ className?: string }>
  title: string
  subtitle?: ReactNode
  /**
   * Operational state, when the page has one worth showing — a paused market, a closed
   * valve. Omitted rather than defaulted to `live`: a lamp on every page, always green,
   * would be decoration, and the whole point of the lamp is that it changes.
   */
  state?: LampState
  action?: ReactNode
}) {
  // Looked up by the icon's stable id, not its identity — see the note in ui/page-art.tsx
  // for the two ways an identity-keyed map failed silently.
  // Publishes this page's identity upward. The bar no longer reads it — it stopped rendering
  // the title once the hero below became the one place it appears — but the publish itself is
  // kept per a separate, already-logged decision; see layout/page-meta.tsx.
  usePublishPageMeta({ icon: Icon, title })

  const Art = PAGE_ART[(Icon as NavIcon).iconId ?? ''] ?? Icon

  return (
    <div className="relative mb-4 overflow-hidden">
      {/*
       * ── The dotted field ──
       *
       * The reference lays a grid of accent dots behind its page header and fades it out
       * toward the right, so the title sits on texture and the space beside it does not.
       * It is drawn as a radial-gradient tile rather than an asset: at this size an image
       * would be a network request for something the browser can compute, and a tile scales
       * to any header width without a second file.
       *
       * The mask is what keeps it from reading as wallpaper. Unfaded, a dot field behind a
       * heading competes with the heading; faded, it reads as the page starting.
       */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle at center, var(--color-accent-wash) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
          // Prefixed first for older WebKit — see the same convention on `body::before` in
          // theme.css.
          WebkitMaskImage: 'linear-gradient(to right, black, transparent 62%)',
          maskImage: 'linear-gradient(to right, black, transparent 62%)',
        }}
      />

      <div className="relative flex flex-wrap items-start justify-between gap-x-4 gap-y-3 py-2">
        {/* 14px gap between glyph and text — the reference's measurement. */}
        <div className="flex min-w-0 items-center gap-3.5">
          <Art className="size-9 shrink-0" />
          <div className="min-w-0">
            {/* The one `title-page` on the screen — 20/30 w700, the ladder's top step. */}
            <h1 className="title-page truncate">{title}</h1>
            {subtitle && (
              /*
               * 14/21 w400 muted — prose weight, like every other subtitle and note in the app.
               *
               * This was w600, defended on the grounds that at w400 beside a w800 title it read
               * as body copy that had wandered into the header. That was a fair reading of a
               * problem the TITLE was causing: the fix for an 800 title is not a 600 subtitle,
               * it is a 700 title. With the title corrected the subtitle has nothing to shout
               * over, and the ladder's own corollary applies again — a semibold subtitle under
               * a bold title is two headings, and the reader cannot tell which to read first.
               *
               * Leading is omitted deliberately: `--text-sm--line-height` already binds 1.5 to
               * this size, so `leading-[21px]` only restated the token.
               */
              <p className="text-sm text-muted">{subtitle}</p>
            )}
          </div>
        </div>

        {(state || action) && (
          <div className="flex shrink-0 items-center gap-3 pt-1.5">
            {state && <Lamp state={state} />}
            {action}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A row of headline figures, each in its own housing.
 *
 * ── One housing per figure, not one housing for the row ──
 * The reference draws these six stats inside a single card, on one uninterrupted ground —
 * see the note that used to sit here, which argued exactly that: read together, a shared
 * surface with no hairlines between them is the more honest shape, and separate cards spend
 * a gap and a ring on numbers that belong in the same glance.
 *
 * This was overruled on the user's explicit instruction: each figure gets its own card. That
 * is a deliberate departure from the reference, not an oversight — recorded here so the next
 * pass doesn't "fix" it back to a single housing. Each item below reuses `Housing` so the
 * card treatment (surface step, hairline ring, top-edge highlight) stays defined once in
 * `./Card` rather than hand-rolled per figure.
 *
 * This replaces four different treatments that were doing this job: the centred 48px hero on
 * Markets, the near-identical one on Lite, Lend's bare `StatBlock` row, and Deposit's
 * hand-rolled treasury strip. All four showed a label and a number; none of them agreed on
 * how big, how aligned, or on what ground.
 *
 * `cols` is capped at four because a fifth figure at this size stops being a headline.
 */
export function MetricRow({
  title,
  note,
  items,
  loading = false,
  className,
}: {
  /**
   * Renders an in-card header above the stats grid, title left and `note` right — the same
   * row shape as `CardHeader`. Omitted by every call site that used an external `SectionHead`
   * before this row grew its own; those keep rendering exactly as they did.
   */
  title?: ReactNode
  /** The header row's right-aligned note. Only rendered when `title` is present. */
  note?: ReactNode
  items: {
    label: ReactNode
    value: ReactNode
    unit?: string
    note?: ReactNode
    /**
     * Semantic colour for the figure. Carried over from the pre-redesign MetricRow, which
     * every page still passes: brass for reward, fluid for a stablecoin, fault for a warning.
     * Defaults to `ink`, which resolves to `text-figure` — the colour this row hardcoded
     * before the prop came back, so an item that omits it renders exactly as it did.
     */
    tone?: Tone
  }[]
  /**
   * Show the loading mark instead of the figures, while keeping the section's own box.
   *
   * The caller passes the flag for THIS section's data, not the page's. That distinction is
   * the whole point: a page whose sections each wait on their own query renders each one as
   * it arrives, instead of holding all of them behind the slowest.
   */
  loading?: boolean
  className?: string
}) {
  /*
   * Columns are chosen to DIVIDE the item count, not to fill the widest row possible.
   *
   * The first version just used four columns for anything with four or more items. Home
   * passes six, so it drew 4 + 2 and left two empty cells — six figures read as "four figures
   * and a hole".
   *
   * Preferring a divisor means six becomes 3 × 2, which is also the shape the reference gives
   * its six-stat header. Counts with no good divisor (five, seven) fall back to three and
   * accept a short last row, which is still better than an uneven grid.
   */
  const n = items.length
  const cols =
    n <= 2
      ? 'sm:grid-cols-2'
      : n === 3
        ? 'sm:grid-cols-3'
        : n === 4
          ? 'sm:grid-cols-2 lg:grid-cols-4'
          : n % 3 === 0
            ? 'sm:grid-cols-2 lg:grid-cols-3'
            : 'sm:grid-cols-2 lg:grid-cols-4'

  return (
    <div className={className}>
      {title && (
        // Title left, note right — the section heading that used to sit inside the shared
        // card's own inset now sits above the grid, since there is no longer one card for it
        // to be a header of. It takes the same `.title-section` class as `CardHeader`, so a
        // titled MetricRow reads as the same kind of section as a titled ChartSection — and
        // the two cannot drift apart, because there is only one definition of the style.
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2 className="title-section">{title}</h2>
          {note && <div className="shrink-0 text-sm text-muted">{note}</div>}
        </div>
      )}
      {/*
       * ── A card per figure ──
       *
       * Each item is its own `Housing`, but with the hairline ring dropped (`ring-0`).
       *
       * Twelve cards means twelve outlines where the shared housing had one, and at this
       * density the rings stop reading as edges and start reading as noise — a grid of boxes
       * rather than a row of figures. The surface step does the separating instead: `surface`
       * sits 1.341 contrast above the ground, which is the ladder the dark palette was
       * rederived to make visible precisely so a card could be told from the page without a
       * line around it. The top-edge highlight stays, so each card still catches the light on
       * the edge facing it.
       *
       * 12px between cards (`gap-3`) is the reference's gutter — the same spacing `CardBody`
       * uses for content inset, reused here as the space between cards instead of within one.
       *
       * `items-stretch` (the grid default) keeps every card in a row the same height even
       * though only some items — Deposits, with its depositor-count note — carry a third
       * line. The shorter cards get that height as breathing room under the figure rather
       * than shrinking to fit their own content, so the row stays level.
       */}
      {/*
       * ── The loading box is sized from the grid it replaces ──
       *
       * Not an arbitrary height. `rows` is what the real grid will lay out at the widest
       * breakpoint, and CARD_H is the Housing's own height at this type scale — label at
       * 21px, figure at 36px, 12px of padding top and bottom. Multiplying them reserves very
       * nearly the box the figures will occupy, so the page does not jump when they land.
       *
       * This is the same rule ui/primitives.tsx states for Skeleton, applied to a section
       * rather than a line: a placeholder of the wrong height is worse than none at all.
       */}
      {loading ? (
        <div
          className="grid place-items-center rounded-[20px] bg-surface ring-1 ring-line"
          style={{ minHeight: metricBoxHeight(n) }}
        >
          <RoutedMark size="xl" />
        </div>
      ) : (
      <div className={cn('grid gap-3', cols)}>
        {items.map((item, i) => (
          <Housing key={i} className="min-w-0 p-3 ring-0">
            {/* 14/21 w400 muted — the stat label. Leading comes from the size token. */}
            <div className="text-sm text-muted">{item.label}</div>
            {/* 24/36 w600, with the denomination trailing it on the same baseline. */}
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  'display text-2xl font-semibold leading-9',
                  TONE_TEXT[item.tone ?? 'ink'],
                )}
              >
                {item.value}
              </span>
              {item.unit && <Badge tone="unit">{item.unit}</Badge>}
            </div>
            {item.note && <div className="mt-1 text-xs text-faint">{item.note}</div>}
          </Housing>
        ))}
      </div>
      )}
    </div>
  )
}

/** Housing height at this type scale: 12 + 21 + 2 + 36 + 12. */
const METRIC_CARD_H = 83

/** Rows the widest breakpoint lays out, mirroring the `cols` ladder above. */
function metricBoxHeight(n: number): number {
  const perRow = n <= 2 ? n : n === 3 ? 3 : n === 4 ? 4 : n % 3 === 0 ? 3 : 4
  const rows = Math.ceil(n / perRow)
  return rows * METRIC_CARD_H + (rows - 1) * 12
}

/**
 * A chart with its figures beside it, in one card.
 *
 * ── The shape, and why it is worth copying ──
 * This is the reference's section: one card whose header row holds the title with a range
 * control pushed right — "Total Value Locked" beside "Last 5 Days" — and whose body holds a
 * column of filled stat tiles down the left and the chart filling the rest. It runs that
 * layout twice on its home page, unchanged, for two completely different subjects.
 *
 * The header used to be a `SectionHead` sitting OUTSIDE the card, an uppercase mono eyebrow
 * with a hairline rule to the note. That put the title in the gap between cards rather than
 * on the card it names, which is the opposite of what the reference does. It is now the same
 * in-card header row `CardHeader` renders — title left, `action` and `note` right, on the
 * card's own inset — with the tiles and chart sitting directly beneath it.
 *
 * What makes it work is the relationship between the two halves — the tiles are the
 * BREAKDOWN and the chart is the aggregate MOVING. You read the composition on the left and
 * the trend on the right without the page having to say so. A row of stats above a chart
 * implies nothing; a column beside one implies "these are its parts".
 *
 * ── Why the tiles are filled, when `MetricRow`'s are not ──
 * `MetricRow` sits on the page as the headline band, where a filled cell would compete with
 * the page header. These sit INSIDE a panel, next to a chart, where an unfilled figure just
 * looks like loose text that happens to be left of a graph. The fill is what groups them
 * into a column and separates them from the plot area.
 */
export function ChartSection(props: ChartSectionProps) {
  /*
   * The provider wraps the section rather than living inside it: `ChartSectionInner` READS the
   * focus to light a tile, and a component cannot consume a context it renders. Same split, and
   * for the same reason, as PageMetaProvider in layout/AppShell.
   */
  return (
    <ChartFocusProvider>
      <ChartSectionInner {...props} />
    </ChartFocusProvider>
  )
}

interface ChartSectionProps {
  title: ReactNode
  note?: ReactNode
  /** A range picker or refresh control. Sits at the end of the section rule. */
  action?: ReactNode
  /*
   * No per-tile `tone`, and no exception either. These are the chart's BREAKDOWN, read as a set
   * against one plot, and colouring them individually makes the set look like unrelated figures
   * that happen to be stacked.
   *
   * ── There WAS an exception, and why it is gone ──
   * A `series` flag marked the tile whose value the chart actually plots — on Home, "Total
   * deposited" reads 278.1K and the line ends at 278.1K, the same number twice — and tinted
   * that one tile with the series colour so the figure and the stroke read as one quantity.
   *
   * The argument was sound and the result was not. In a column of four tiles, one coloured and
   * three neutral reads as "this figure is special" long before it reads as "this figure is the
   * one on the chart", and the reader has to already understand the convention for the colour
   * to say anything at all. It got louder, not clearer, when the ramp moved to blue: the tinted
   * figure then matched the accent, so it also looked like a link.
   *
   * The link survives without it. The chart labels its own end point with the same number,
   * inches away, which states the correspondence directly instead of encoding it. `Readout`
   * keeps a `series` tone for cases where a swatch genuinely has to identify one line among
   * several; a single-series chart is not one of those.
   */
  tiles: {
    label: ReactNode
    value: ReactNode
    unit?: string
    /**
     * Index of the chart point this tile describes, if it describes exactly one.
     *
     * "Total deposited" is the line's end point and "Largest" is one particular spike; hovering
     * either rings that marker, and hovering the marker lifts the tile back. "Depositors" and
     * "Average" are properties of the whole set with no single point to indicate, so they omit
     * this and stay inert — a tile that lights up without anything lighting up beside it would
     * promise a link it cannot deliver.
     */
    point?: number
  }[]
  /**
   * Show the loading mark in the body while this section's series is in flight.
   *
   * The HEADER keeps rendering — title, note and any refresh control. That is deliberate and
   * it is what the reference does too: the card's identity is known before its data is, so
   * hiding the title as well would make the section briefly anonymous and cost the reader the
   * one cue that tells them what they are waiting for.
   */
  loading?: boolean
  /** The chart. */
  children: ReactNode
}

function ChartSectionInner({
  title,
  note,
  action,
  tiles,
  loading = false,
  children,
}: ChartSectionProps) {
  const { focus, setFocus } = useChartFocus()

  return (
    <Housing className="overflow-hidden">
      <CardHeader
        title={title}
        action={
          (note || action) && (
            <div className="flex items-center gap-3">
              {note && <span className="text-sm text-muted">{note}</span>}
              {action}
            </div>
          )
        }
      />
      {/*
       * The tile column is fixed-width and the chart takes the rest, rather than both
       * being fractions. A chart that shrinks with its legend column reflows its axis
       * labels at every breakpoint; a fixed column means only one side is ever moving.
       *
       * Below lg the column becomes a horizontal row of tiles above the chart — stacking
       * four tiles vertically on a phone would push the chart off the screen entirely.
       */}
      {loading ? (
        /*
         * 288px is the loaded body's own height — a 240px chart plus the 3+3 padding and the
         * gap around it — so the card does not resize under the reader when the series lands.
         */
        <div className="grid min-h-[288px] place-items-center">
          <RoutedMark size="xl" />
        </div>
      ) : (
      <div className="grid gap-4 p-3 lg:grid-cols-[minmax(0,230px)_minmax(0,1fr)] lg:gap-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-1 lg:content-start">
          {tiles.map((t, i) => (
            /*
             * radius 20 and 12px of padding — the same values as the card holding them.
             * The reference deliberately does not step the tile's radius down; see the
             * radius note in theme.css for why that flatness is what makes the two read
             * as one material.
             */
            /*
             * ── The tile half of the tile ↔ chart link ──
             *
             * A tile that names ONE point on the line becomes a pointer to it. Hovering here
             * publishes that index; `BondFlowChart` reads it and rings the marker. Hovering the
             * marker publishes the same index back and lights this tile. One shared number
             * drives both directions, which is why neither side needs to know about the other.
             *
             * Tiles without a `point` — "Depositors", "Average" — set nothing and light up for
             * nothing. They are properties of the whole set, and a tile that responds to the
             * cursor while pointing at no particular thing is a promise the section cannot keep.
             *
             * `onFocus`/`onBlur` alongside the pointer handlers so the link is reachable from
             * the keyboard; `tabIndex` only when there is something to reveal, because a tile
             * that stops the tab order to show nothing is a tax on every keyboard user.
             */
            <div
              key={i}
              onPointerEnter={t.point === undefined ? undefined : () => setFocus(t.point!)}
              onPointerLeave={t.point === undefined ? undefined : () => setFocus(null)}
              onFocus={t.point === undefined ? undefined : () => setFocus(t.point!)}
              onBlur={t.point === undefined ? undefined : () => setFocus(null)}
              tabIndex={t.point === undefined ? undefined : 0}
              className={cn(
                'rounded-[20px] bg-elevated p-3 ring-1',
                /*
                 * `translate`, NOT `transform`.
                 *
                 * Tailwind v4 compiles `-translate-y-px` to the standalone `translate` CSS
                 * property rather than to a `transform: translateY()`. Naming `transform` in the
                 * transition list therefore animates a property this element never sets, and the
                 * lift snaps to its final position instead of easing into it — visible as a tiny
                 * jolt, and the kind of thing that reads as jank without being locatable.
                 * Verified in the browser: computed `translate` is `0px -1px`, `transform` is
                 * `none`. `box-shadow` covers the ring, which v4 paints as a shadow layer.
                 */
                'transition-[box-shadow,translate] duration-150',
                /*
                 * A 1px rise and the ring stepping from `line` to `accent-edge`. Deliberately
                 * small: motion.ts's `pressable` uses the same 1px, and anything larger reads as
                 * the tile being pressed rather than as it being pointed at.
                 *
                 * `motion-reduce:translate-y-0` because the global reduced-motion block in
                 * theme.css zeroes transition DURATION but not the transform itself — without
                 * this the tile would still jump, just instantly, which is the one outcome a
                 * reader who asked for less motion did not want. The ring change survives, so
                 * the link still reads.
                 */
                t.point !== undefined && focus === t.point
                  ? 'ring-accent-edge -translate-y-px motion-reduce:translate-y-0'
                  : 'ring-line',
                t.point !== undefined && 'cursor-default outline-none',
              )}
            >
              <div className="flex items-center gap-1 text-sm text-muted">
                {t.label}
              </div>
              {/* 20/30 w600 — one step under the metrics card's figure, because these are
                  the breakdown and it is the headline. */}
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                {/*
                  * Every tile takes `figure`. There is no coloured one any more — see the
                  * `tiles` note above for what was traded away and what still carries the link.
                  */}
                <span className="display text-xl font-semibold leading-[30px] text-figure">
                  {t.value}
                </span>
                {t.unit && <Badge tone="unit">{t.unit}</Badge>}
              </div>
            </div>
          ))}
        </div>

        <div className="min-w-0 overflow-x-auto">{children}</div>
      </div>
      )}
    </Housing>
  )
}
